import { cn } from "cn";
import { type VariantProps, cva } from "class-variance-authority";
import Link from "next/link";
import * as React from "react";

const AlertContext =
  React.createContext<VariantProps<typeof alertVariants>["variant"]>("default");

const alertVariants = cva(
  "relative w-full rounded-xl border px-4 py-[10px] text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-3 [&>svg]:opacity-70 [&>svg]:top-3 [&>svg]:size-5 [&>svg]:text-foreground [&>svg~*]:pl-7 ",
  {
    variants: {
      variant: {
        default: "bg-neutral-50 dark:bg-neutral-900",
        warning: "bg-orange-50 text-orange-950 border-orange-200 dark:bg-orange-950 dark:text-orange-100 dark:border-orange-800",
        info: "bg-primary-50 text-primary-950 border-primary-200 dark:bg-primary-950 dark:text-primary-100 dark:border-primary-800",
        destructive: "bg-rose-50 text-rose-950 border-rose-200 dark:bg-rose-950 dark:text-rose-100 dark:border-rose-800",
      },
      icon: {
        filled: "[&>svg]:fill-current",
        stroke: "",
      },
    },
    defaultVariants: {
      variant: "default",
      icon: "filled",
    },
  },
);

const alertTint = {
  default: undefined,
  warning: "warning",
  info: undefined,
  destructive: "destructive",
} as const;

const alertLinkVariants = cva(
  "underline mt-[10px] inline-block mb-1 hover:opacity-70 transition-opacity font-medium",
  {
    variants: {
      variant: {
        default: "text-neutral-900 dark:text-neutral-100",
        warning: "text-orange-950 dark:text-orange-100",
        info: "text-primary-950 dark:text-primary-100",
        destructive: "text-rose-950 dark:text-rose-100",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = ({
  className,
  variant,
  icon,
  ...props
}: {} & VariantProps<typeof alertVariants> & {
  className?: string;
  children: React.ReactNode;
}) => (
  <AlertContext.Provider value={variant}>
    <div
      role="alert"
      data-tint={alertTint[variant ?? "default"]}
      className={cn(alertVariants({ variant, icon }), className)}
      {...props}
    />
  </AlertContext.Provider>
);
Alert.displayName = "Alert";

const AlertTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h5 className={cn("mb-1 font-medium leading-normal", className)} {...props} />
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <div className={cn("text-pretty", className)} {...props} />
);
AlertDescription.displayName = "AlertDescription";

const AlertLink = ({
  className,
  ...props
}: React.ComponentProps<typeof Link> & { className?: string }) => {
  const variant = React.useContext(AlertContext);
  return (
    <Link
      className={cn(alertLinkVariants({ variant }), className)}
      target="_blank"
      {...props}
    />
  );
};
AlertLink.displayName = "AlertLink";

export { Alert, AlertDescription, AlertLink, AlertTitle };
